import React, { useEffect, useRef, useState } from 'react';
import Icon from './icons';
import { FP, OFFICES, LOCKED_OFFICE } from './types';
import { BOND_AXIS, TermSeg, WITHDRAW_AXIS } from './terms';
import type { RegState, Co } from './types';
import { searchCompanies, createCompany, ensureFp, ensureWm, listFps, listWms, listInsuranceCompanies } from './api';
import type { TmCompany, TmFp, TmWm } from './api';
import CoTermsFields from './CoTermsFields';
import { coAmtDefaults } from './coterms';

interface Props {
  reg: RegState;
  onField: (k: keyof RegState, v: string) => void;
  onCo: (i: number, f: keyof Co, v: string) => void;
  onCoPatch: (i: number, patch: Partial<Co>) => void;
  /** 여러 법인을 한 번에 고치기 — 칸마다 patch 또는 null(그대로) · 투자금액 기본값 채우기용 */
  onCoPatches: (patches: Array<Partial<Co> | null>) => void;
  /** 등록 법인 몫에서 뺄 금액 — 예시 이름(임우승 등) LP 출자금 합계 · 그 LP 가 들고 올 다른 법인 몫 */
  reserved?: number;
  onAddCo: () => void;
  onRemoveCo: (i: number) => void;
  onCancel?: () => void;
  onSubmit?: () => void;
  mode?: 'create' | 'edit';
}

export default function VentureRegister({ reg, onField, onCo, onCoPatch, onCoPatches, reserved = 0, onAddCo, onRemoveCo, onCancel, onSubmit, mode }: Props) {
  const editMode = mode === 'edit';
  const [ins, setIns] = useState<string[]>(FP);
  const [fps, setFps] = useState<TmFp[] | null>(null);
  const [wms, setWms] = useState<TmWm[] | null>(null);
  const [coDrop, setCoDrop] = useState<{ i: number; rows: TmCompany[] } | null>(null);
  const [fpDrop, setFpDrop] = useState<{ i: number; rows: TmFp[] } | null>(null);
  const [wmDrop, setWmDrop] = useState<{ i: number; rows: TmWm[] } | null>(null);
  const timer = useRef<number | null>(null);
  const autoRegRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    listInsuranceCompanies().then((rows) => { if (rows.length) setIns(rows); }).catch(() => undefined);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, []);

  // 신규 법인(사업자번호 10자리 완성·미연동)을 업무관리(TM) 업체로 자동 등록 + 입력된 FP·WM도 함께 생성/연동
  useEffect(() => {
    reg.cos.forEach((co, i) => {
      const digits = (co.bizno || '').replace(/\D/g, '');
      if (co.companyId || !co.name || !co.name.trim() || digits.length !== 10 || autoRegRef.current.has(digits)) return;
      autoRegRef.current.add(digits);
      (async () => {
        const fpId = co.fpId || (co.fpName && co.fpName.trim() ? await ensureFp({ name: co.fpName, phone: co.fpTel, company: co.fpCo }) : '') || '';
        const wmId = co.wmId || (co.wm && co.wm.trim() ? await ensureWm({ name: co.wm }) : '') || '';
        const id = await createCompany({ name: co.name.trim(), biz_no: co.bizno.trim(), ceo: co.ceo || '', ceo_phone: co.mgrTel || '', fp_ids: fpId ? [fpId] : [], wm_id: wmId || null });
        const patch: Partial<Co> = {};
        if (id) { patch.companyId = id; patch.existing = true; }
        if (fpId && !co.fpId) patch.fpId = fpId;
        if (wmId && !co.wmId) patch.wmId = wmId;
        if (Object.keys(patch).length) onCoPatch(i, patch);
      })().catch(() => undefined);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reg.cos]);

  // 투자금액 기본값 — 법인 1곳이면 총액 전부, 2곳 이상이면 반반(나눠서) · 직접 고친 칸은 그대로 (2026-10-31 지도사)
  const totalN = parseInt((reg.total || '').replace(/[^0-9]/g, ''), 10) || 0;
  useEffect(() => {
    const patches = coAmtDefaults(totalN - reserved, reg.cos).map((d, i): Partial<Co> | null => {
      if (d == null) return null;
      const v = d.toLocaleString();
      return reg.cos[i].amt !== v || !reg.cos[i].amtAuto ? { amt: v, amtAuto: true } : null;
    });
    if (patches.some(Boolean)) onCoPatches(patches);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalN, reserved, reg.cos.length, reg.cos.map((c) => (c.amtAuto ? '' : c.amt)).join('|')]);

  const loadFps = () => { if (fps === null) { setFps([]); listFps().then(setFps).catch(() => setFps([])); } };
  const loadWms = () => { if (wms === null) { setWms([]); listWms().then(setWms).catch(() => setWms([])); } };

  const coNameChange = (i: number, v: string) => {
    onCoPatch(i, { name: v, companyId: null, existing: false });
    if (timer.current) window.clearTimeout(timer.current);
    if (!v.trim()) { setCoDrop(null); return; }
    timer.current = window.setTimeout(() => {
      searchCompanies(v.trim()).then((rows) => setCoDrop(rows.length ? { i, rows } : null)).catch(() => setCoDrop(null));
    }, 250);
  };
  const pickCo = (i: number, r: TmCompany) => {
    onCoPatch(i, {
      name: r.name,
      bizno: r.biz_no || reg.cos[i].bizno || '',
      ceo: r.ceo || reg.cos[i].ceo || '',
      mgrTel: reg.cos[i].mgrTel || r.ceo_phone || '',
      companyId: r.id,
      existing: true,
    });
    setCoDrop(null);
  };
  const fpNameChange = (i: number, v: string) => {
    onCoPatch(i, { fpName: v, fpId: '' });
    if (!v.trim()) { setFpDrop(null); return; }
    const pool = fps || [];
    const rows = pool.filter((f) => f.name && f.name.includes(v.trim())).slice(0, 8);
    setFpDrop(rows.length ? { i, rows } : null);
  };
  const pickFp = (i: number, f: TmFp) => {
    const patch: Partial<Co> = { fpName: f.name, fpId: f.id };
    if (f.phone) patch.fpTel = f.phone;
    if (f.company && (ins.includes(f.company) || !reg.cos[i].fpCo)) patch.fpCo = f.company;
    onCoPatch(i, patch);
    setFpDrop(null);
  };
  const wmNameChange = (i: number, v: string) => {
    onCoPatch(i, { wm: v, wmId: '' });
    if (!v.trim()) { setWmDrop(null); return; }
    const pool = wms || [];
    const rows = pool.filter((w) => w.name && w.name.includes(v.trim())).slice(0, 8);
    setWmDrop(rows.length ? { i, rows } : null);
  };
  const pickWm = (i: number, w: TmWm) => {
    onCoPatch(i, { wm: w.name, wmId: w.id });
    setWmDrop(null);
  };
  const insOpts = (cur: string): string[] => (cur && !ins.includes(cur) ? [cur, ...ins] : ins);

  const t = parseInt((reg.total || '').replace(/[^0-9]/g, ''), 10) || 0;
  const pe = parseInt((reg.per || '').replace(/[^0-9]/g, ''), 10) || 0;
  const calc = t && pe
    ? `총 출자좌수 = ${(t / pe).toLocaleString()}좌 (출자총액 ÷ 1좌 금액)`
    : '출자총액과 1좌 금액을 입력하면 총 좌수가 계산됩니다';

  return (
    <>
      {!editMode ? (
        <div className="page-h">
          <div>
            <h1>새 벤처인증 프로젝트 등록</h1>
            <p>여기 입력한 정보가 모든 모듈 공통 DB에 저장돼 20종 서류에 자동 채워집니다.</p>
          </div>
        </div>
      ) : null}
      <div className="formcard">
        {editMode
          ? <div className="fc-h"><b>등록정보 수정</b><p>수정 사항은 자동 저장되며 1단계 자료수집·문서 생성에 반영됩니다</p></div>
          : <div className="fc-h"><b>기초 정보 입력</b><p>1단계: 프로젝트 생성 · 표시(*)는 필수</p></div>}
        <div className="fc-b">
          <div className="sec">
            <div className="sec-h"><Icon n="folder" /> 조합 정보</div>
            <div className="grid">
              {/* 진행 주체 — 로움 / 올댓벤처스 건이 한 목록에 섞이므로 등록 때 고른다 (목록 필터·배지, 2026-09-24) */}
              {LOCKED_OFFICE ? null : <div className="fld full office-fld"><label>진행 주체 <span style={{ fontWeight: 400, color: 'var(--muted)' }}>· 목록에서 사무소별로 구분됩니다</span></label>
                <div className="seg office-seg" role="radiogroup" aria-label="진행 주체">
                  {OFFICES.map((o) => <button key={o.v} type="button" className={(reg.office || 'roum') === o.v ? 'on' : ''} aria-pressed={(reg.office || 'roum') === o.v} title={o.name} onClick={() => onField('office', o.v)}>{o.name}</button>)}
                </div>
              </div>}
              <div className="fld full"><label>조합명칭 <span className="req">*</span></label><input value={reg.org} onChange={(e) => onField('org', e.target.value)} placeholder="예) 로움개인투자조합1호" /></div>
              {/* 투자 처리 방법 — 조합 단위(2026-10-19 지도사 「회사 단위가 아니라 조합 단위로」) · 처음 등록 때 꼭(모르면 「미정」) */}
              <div className={'fld full inv-plan' + (!editMode && !(reg.investPlan || '').trim() ? ' need' : '')}>
                <label>투자 처리 방법 {editMode ? null : <span className="req">*</span>}<span className="lbl-sub"> · 이 조합의 투자를 어떻게 처리하기로 했는지 (우리만 봄)</span></label>
                <textarea rows={2} maxLength={500} value={reg.investPlan || ''} onChange={(e) => onField('investPlan', e.target.value)}
                  placeholder="예) 전환사채로 투자 → 벤처확인서 받은 뒤 바로 회수 · 보통주로 그대로 유지 · 대표님이 재인수 (모르면 「미정」)" />
              </div>
              {/* 이체 구분(2026-10-28 지도사 「조합 통장 입금 후 법인 통장으로 바로 이체할 조합 · 2주 대기할 조합 구분 · LP 매칭할 때 참고」) */}
              <div className="fld full"><label>이체 구분<span className="lbl-sub"> · 조합 통장에 입금한 뒤 법인 통장으로 언제 보내는지 · LP 매칭할 때 봐요</span></label>
                <TermSeg axis={WITHDRAW_AXIS} value={reg.withdraw || ''} onChange={(v) => onField('withdraw', v)} />
              </div>
              {/* 사채 처리(2026-10-28 지도사 「전환사채 발행한 것에 대해서 소득공제 신청인지 바로 회수인지」) */}
              <div className="fld full"><label>사채 처리<span className="lbl-sub"> · 전환사채로 투자한 금액을 소득공제 신청하는지 바로 회수하는지 · LP 매칭할 때 봐요</span></label>
                <TermSeg axis={BOND_AXIS} value={reg.bond || ''} onChange={(v) => onField('bond', v)} />
              </div>
              <div className="fld full"><label>조합 소재지</label><input value={reg.addr} onChange={(e) => onField('addr', e.target.value)} placeholder="시·군·구 도로명주소" /></div>
              <div className="fld"><label>출자금 총액 (원)</label><input value={reg.total} onChange={(e) => onField('total', e.target.value)} placeholder="100,000,000" /></div>
              <div className="fld"><label>1좌 금액 (원)</label><input value={reg.per} onChange={(e) => onField('per', e.target.value)} /></div>
              <div className="fld full"><div className="calc">{calc}</div></div>
              <div className="fld"><label>존속기간 (년)</label><input value={reg.dur} onChange={(e) => onField('dur', e.target.value)} /></div>
            </div>
          </div>
          <div className="sec">
            <div className="sec-h"><Icon n="user" /> 업무집행조합원 (GP)</div>
            <div className="grid">
              <div className="fld"><label>GP 이름 <span className="req">*</span></label><input value={reg.gp} onChange={(e) => onField('gp', e.target.value)} placeholder="홍길동" /></div>
              <div className="fld"><label>GP 연락처</label><input value={reg.gpTel} onChange={(e) => onField('gpTel', e.target.value)} placeholder="010-0000-0000" /></div>
              <div className="fld full"><label>GP 이메일</label><input value={reg.gpEmail} onChange={(e) => onField('gpEmail', e.target.value)} placeholder="gp@example.com" /></div>
            </div>
          </div>
          <div className="sec" style={{ marginBottom: 0 }}>
            <div className="sec-h"><Icon n="build" /> 투자법인 <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}>· 2개 이상이면 벤처확인서도 그만큼</span></div>
            <div className="note" style={{ background: 'var(--accent-soft)', marginBottom: 14, marginTop: 0 }}>
              <Icon n="info" style={{ color: 'var(--accent)' }} />
              <div><b style={{ color: 'var(--accent-ink)' }}>모든 모듈 공통 업체 DB</b><span style={{ color: 'var(--accent-ink)' }}> 기존 업체는 법인명을 입력하면 검색되어 선택 시 자동 연동, 신규 업체는 그대로 직접 입력하면 됩니다.</span></div>
            </div>
            {reg.cos.map((c, i) => (
              <div className="cocard" key={i}>
                <div className="cocard-top">
                  <div className="cosearch" style={{ position: 'relative' }}>
                    <input
                      value={c.name}
                      autoComplete="off"
                      onChange={(e) => coNameChange(i, e.target.value)}
                      onBlur={() => window.setTimeout(() => setCoDrop(null), 180)}
                      placeholder={i === 0 ? '법인명 입력 · 기존 업체는 검색 선택, 없으면 직접 입력' : '법인명 입력 (검색 선택 또는 직접 입력)'}
                    />
                    {coDrop && coDrop.i === i ? (
                      <div className="codrop show">
                        {coDrop.rows.map((r) => (
                          <div className="dropitem" key={r.id} onMouseDown={() => pickCo(i, r)}>
                            <b>{r.name}</b> <span style={{ color: 'var(--muted)', fontSize: 11 }}>{r.biz_no || ''}{r.ceo ? ' · ' + r.ceo : ''}</span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  {c.companyId
                    ? <span className="pill p-green" style={{ whiteSpace: 'nowrap' }}>기존 업체 연동</span>
                    : c.name ? <span className="pill p-amber" style={{ whiteSpace: 'nowrap' }}>신규 업체</span> : null}
                  {reg.cos.length > 1 ? <button className="del" onClick={() => onRemoveCo(i)}><Icon n="x" /></button> : null}
                </div>
                <div className="cogrid">
                  <div className="fld"><label>사업자등록번호 <span className="req">*</span></label><input value={c.bizno} onChange={(e) => onCo(i, 'bizno', e.target.value)} placeholder="000-00-00000" /></div>
                  <div className="fld"><label>대표자</label><input value={c.ceo} onChange={(e) => onCo(i, 'ceo', e.target.value)} placeholder="대표자명" /></div>
                  <div className="fld"><label>담당자 연락처</label><input value={c.mgrTel} onChange={(e) => onCo(i, 'mgrTel', e.target.value)} placeholder="010-0000-0000" /></div>
                  <div className="fld"><label>투자금액 (원){c.amtAuto ? <span className="lbl-sub">{reserved > 0 ? ' · 자동(OOO LP 몫 제외)' : reg.cos.length > 1 ? ' · 자동(나눠서)' : ' · 자동(전액)'}</span> : null}</label><input value={c.amt} onChange={(e) => onCoPatch(i, { amt: e.target.value, amtAuto: false })} placeholder="50,000,000" title={c.amtAuto ? '자동 기본값 — 법인 수대로 나눈 금액 · 고치면 그대로 둡니다' : undefined} /></div>
                  <div className="fld" style={{ position: 'relative' }}>
                    <label>소개 FP 이름</label>
                    <input
                      value={c.fpName}
                      autoComplete="off"
                      onFocus={loadFps}
                      onChange={(e) => fpNameChange(i, e.target.value)}
                      onBlur={() => window.setTimeout(() => setFpDrop(null), 180)}
                      placeholder="김보험 · 업무관리 FP 자동완성"
                    />
                    {fpDrop && fpDrop.i === i ? (
                      <div className="codrop show">
                        {fpDrop.rows.map((f) => (
                          <div className="dropitem" key={f.id} onMouseDown={() => pickFp(i, f)}>
                            <b>{f.name}</b> <span style={{ color: 'var(--muted)', fontSize: 11 }}>{f.company || ''}{f.phone ? ' · ' + f.phone : ''}</span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className="fld"><label>FP 연락처</label><input value={c.fpTel} onChange={(e) => onCo(i, 'fpTel', e.target.value)} placeholder="010-0000-0000" /></div>
                  <div className="fld"><label>FP 소속(보험사)</label><select value={c.fpCo} onChange={(e) => onCo(i, 'fpCo', e.target.value)}>{insOpts(c.fpCo).map((f) => <option key={f}>{f}</option>)}</select></div>
                  <div className="fld" style={{ position: 'relative' }}>
                    <label>담당 WM</label>
                    <input
                      value={c.wm}
                      autoComplete="off"
                      onFocus={loadWms}
                      onChange={(e) => wmNameChange(i, e.target.value)}
                      onBlur={() => window.setTimeout(() => setWmDrop(null), 180)}
                      placeholder="자산관리사 이름 · 업무관리 WM 자동완성"
                    />
                    {wmDrop && wmDrop.i === i ? (
                      <div className="codrop show">
                        {wmDrop.rows.map((w) => (
                          <div className="dropitem" key={w.id} onMouseDown={() => pickWm(i, w)}>
                            <b>{w.name}</b> <span style={{ color: 'var(--muted)', fontSize: 11 }}>{w.company || ''}{w.phone ? ' · ' + w.phone : ''}</span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
                <CoTermsFields co={c} onPatch={(patch) => onCoPatch(i, patch)} />
              </div>
            ))}
            <button className="addbtn" onClick={onAddCo}><Icon n="plus" /> 법인 추가</button>
            <div className="note"><Icon n="info" /><div><b>등록 즉시 업무관리·자료수집 연동</b><span> 진행상황 자동 추적이 켜지고, GP·법인에게 자료수집 요청 링크를 보낼 수 있습니다.</span></div></div>
          </div>
        </div>
        {!editMode ? (
          <div className="fc-f">
            <button className="btn" onClick={() => onCancel && onCancel()}>취소</button>
            <button className="btn pri" onClick={() => onSubmit && onSubmit()}><Icon n="check" /> 프로젝트 등록</button>
          </div>
        ) : null}
      </div>
    </>
  );
}
